import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime } from '../ui/format';
import { Badge, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column } from '../ui/DataTable';
import {
  downloadSubjectExport,
  eraseSubject,
  listPrivacyRequests,
  privacyRegistry,
  type PrivacyRequest,
  type RegistryEntry,
  type SubjectType,
} from './privacy-api';

/**
 * Answering a data subject request (LGPD). Phase 66.
 *
 * The screen somebody opens when a customer writes in asking for their data, or asking to be
 * forgotten. Three things it does on purpose:
 *
 * **The person is found by the address they wrote from.** A subject request arrives as an e-mail,
 * not as a row id, and making somebody look the contact up first is the step where they answer the
 * request about the wrong person.
 *
 * **The reason is required.** Not paperwork: a year later the receipt is what shows the erasure was
 * asked for rather than convenient.
 *
 * **The erasure asks for the address back.** The button is a one-way door, and typing the address
 * is the difference between an irreversible action and an irreversible action somebody meant.
 */

const POLICY_TONE: Record<RegistryEntry['policy'], BadgeTone> = {
  ERASE: 'danger',
  ANONYMISE: 'warning',
  KEEP: 'neutral',
};

const STATUS_TONE: Record<PrivacyRequest['status'], BadgeTone> = {
  COMPLETED: 'positive',
  REFUSED: 'neutral',
};

export function PrivacyPage(): React.JSX.Element {
  const client = useQueryClient();

  const [subjectType, setSubjectType] = useState<SubjectType>('CONTACT');
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const requests = useQuery({ queryKey: ['privacy-requests'], queryFn: listPrivacyRequests });
  const registry = useQuery({
    queryKey: ['privacy-registry'],
    queryFn: privacyRegistry,
    staleTime: 60 * 60_000,
  });

  const ref = { subjectType, email: email.trim(), reason: reason.trim() };

  const exportIt = useMutation({
    mutationFn: () => downloadSubjectExport(ref),
    onSuccess: (archive) => {
      setError(null);
      setDone(t('privacy.exported').replace('{name}', archive.subject.label));
      void client.invalidateQueries({ queryKey: ['privacy-requests'] });
    },
    onError: (caught: unknown) => {
      setDone(null);
      setError(messageFor(caught));
    },
  });

  const erase = useMutation({
    mutationFn: () => eraseSubject({ ...ref, confirm: confirm.trim() }),
    onSuccess: (request) => {
      setError(null);
      setConfirm('');
      setDone(t('privacy.erased').replace('{name}', request.subject_label));
      void client.invalidateQueries({ queryKey: ['privacy-requests'] });
    },
    onError: (caught: unknown) => {
      setDone(null);
      setError(messageFor(caught));
    },
  });

  const incomplete = email.trim() === '' || reason.trim().length < 3;
  const busy = exportIt.isPending || erase.isPending;

  const requestColumns: Column<PrivacyRequest>[] = [
    {
      key: 'subject',
      header: t('privacy.subject'),
      render: (request) => (
        <span className="cell-stack">
          <span className="strong">{request.subject_label}</span>
          <span className="muted small">{t(`privacy.type.${request.subject_type}`)}</span>
        </span>
      ),
    },
    {
      key: 'kind',
      header: t('privacy.kind'),
      render: (request) => (
        <Badge tone={request.kind === 'ERASURE' ? 'danger' : 'info'}>
          {t(`privacy.kind.${request.kind}`)}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: t('channels.status'),
      render: (request) => (
        <Badge tone={STATUS_TONE[request.status]}>{t(`privacy.status.${request.status}`)}</Badge>
      ),
    },
    {
      key: 'reason',
      header: t('privacy.reason'),
      secondary: true,
      render: (request) => <span className="muted small">{request.reason}</span>,
    },
    {
      key: 'affected',
      header: t('privacy.affected'),
      secondary: true,
      align: 'end',
      render: (request) => Object.values(request.affected).reduce((sum, n) => sum + n, 0),
    },
    {
      key: 'when',
      header: t('common.createdAt'),
      secondary: true,
      align: 'end',
      render: (request) => formatDateTime(request.created_at),
    },
  ];

  return (
    <div className="view">
      <PageHeader title={t('nav.privacy')} description={t('privacy.description')} />

      <Panel title={t('privacy.answer')} description={t('privacy.answerHint')}>
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();

            if (!incomplete) exportIt.mutate();
          }}
        >
          <div className="stack__row">
            <label className="stack__field">
              <span>{t('privacy.subjectKind')}</span>
              <select
                className="select"
                value={subjectType}
                onChange={(event) => setSubjectType(event.target.value as SubjectType)}
              >
                <option value="CONTACT">{t('privacy.type.CONTACT')}</option>
                <option value="LEAD">{t('privacy.type.LEAD')}</option>
              </select>
            </label>

            <label className="stack__field">
              <span>
                {t('privacy.email')} <span className="required">*</span>
              </span>
              <input
                type="email"
                value={email}
                maxLength={254}
                placeholder="cliente@exemplo.com.br"
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
          </div>

          <label className="stack__field">
            <span>
              {t('privacy.reason')} <span className="required">*</span>
            </span>
            <input
              value={reason}
              maxLength={500}
              placeholder={t('privacy.reasonPlaceholder')}
              onChange={(event) => setReason(event.target.value)}
            />
            <span className="muted small">{t('privacy.reasonHint')}</span>
          </label>

          {error && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}

          {done && (
            <p className="notice" role="status">
              {done}
            </p>
          )}

          <div className="cell-inline">
            <button
              type="submit"
              className="button button--primary button--compact"
              disabled={incomplete || busy}
            >
              {exportIt.isPending ? t('privacy.exporting') : t('privacy.export')}
            </button>
          </div>
        </form>
      </Panel>

      {/* Its own panel, and below the export, because the order on the screen is the order somebody
          should work in: hand over what you hold, then remove it. */}
      <Panel title={t('privacy.eraseTitle')} description={t('privacy.eraseHint')}>
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();

            if (!incomplete && confirm.trim()) erase.mutate();
          }}
        >
          <label className="stack__field">
            <span>
              {t('privacy.confirm')} <span className="required">*</span>
            </span>
            <input
              value={confirm}
              maxLength={254}
              placeholder={t('privacy.confirmPlaceholder')}
              onChange={(event) => setConfirm(event.target.value)}
            />
            <span className="muted small">{t('privacy.confirmHint')}</span>
          </label>

          <div className="cell-inline">
            <button
              type="submit"
              className="button button--compact button--danger"
              disabled={incomplete || confirm.trim() === '' || busy}
            >
              {erase.isPending ? t('privacy.erasing') : t('privacy.erase')}
            </button>
          </div>
        </form>
      </Panel>

      <Panel title={t('privacy.history')} description={t('privacy.historyHint')}>
        <DataTable
          tableKey="privacy-requests"
          rows={requests.data?.requests}
          columns={requestColumns}
          rowKey={(request) => request.id}
          isLoading={requests.isLoading}
          error={requests.error}
          onRetry={() => void requests.refetch()}
          empty={{ title: t('privacy.noRequests'), description: t('privacy.noRequestsBody') }}
        />
      </Panel>

      {/* Read from the server, so what a tenant is told is what the code will actually do. */}
      <Panel title={t('privacy.policy')} description={t('privacy.policyHint')}>
        <ul className="policy-list">
          {(registry.data?.entries ?? []).map((entry) => (
            <li key={entry.key}>
              <span className="cell-inline">
                <Badge tone={POLICY_TONE[entry.policy]}>{t(`privacy.policy.${entry.policy}`)}</Badge>
                <span className="strong">{t(`privacy.record.${entry.key}`)}</span>
              </span>
              {entry.because && <span className="muted small">{entry.because}</span>}
            </li>
          ))}
        </ul>

        {registry.data && Object.keys(registry.data.excluded).length > 0 && (
          <>
            <p className="muted small">{t('privacy.excluded')}</p>
            <ul className="policy-list">
              {Object.entries(registry.data.excluded).map(([table, because]) => (
                <li key={table}>
                  <span className="cell-inline">
                    <Badge tone="neutral">{t('privacy.policy.KEEP')}</Badge>
                    <code>{table}</code>
                  </span>
                  <span className="muted small">{because}</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Panel>
    </div>
  );
}
